// Import from theme/brand, never from App — App pulls in the whole route tree,
// which makes this a circular import and leaves PRIMARY_BG undefined at runtime.
import { PRIMARY_BG } from '../../../theme/brand';


export default function FormLayerRenderer({ layer }) {
  const data = layer.data;
  const s = data.style;
  const fields = data.fields || [];

  const rawBg        = s?.backgroundColor || '#0f0f1a';
  const bgOpacity    = (s?.backgroundOpacity ?? 90) / 100;
  const bg           = hexToRgba(rawBg, bgOpacity);
  const textColor    = s?.textColor       || '#ffffff';
  const accentColor  = s?.accentColor     || PRIMARY_BG;
  const buttonColor  = s?.buttonColor     || PRIMARY_BG;
  const borderRadius = s?.borderRadius    ?? 12;
  const formTitle   = data.formTitle;

  const inputStyle = {
    width: '100%',
    padding: '7px 10px',
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.18)',
    borderRadius: 7,
    color: textColor,
    fontSize: 12,
    outline: 'none',
    boxSizing: 'border-box',
  };

  const labelStyle = {
    fontSize: 10,
    color: 'rgba(255,255,255,0.55)',
    display: 'block',
    marginBottom: 3,
    fontWeight: 600,
    letterSpacing: 0.3,
  };

  function renderField(field) {
    if (field.type === 'hidden') return null;

    if (field.type === 'submit') {
      return (
        <button key={field.id} style={{
          width: '100%',
          padding: '9px 12px',
          background: buttonColor,
          color: '#fff',
          border: 'none',
          borderRadius: 50,
          fontSize: 13,
          fontWeight: '700',
          cursor: 'pointer',
          letterSpacing: 0.5,
          marginTop: 2,
        }}>
          {field.label || 'Submit'}
        </button>
      );
    }

    if (field.type === 'textarea') {
      return (
        <div key={field.id}>
          {field.label && <label style={labelStyle}>{field.label}{field.required && <span style={{ color: accentColor }}> *</span>}</label>}
          <textarea
            readOnly
            placeholder={field.placeholder}
            style={{ ...inputStyle, resize: 'none', height: 60, fontFamily: 'inherit' }}
          />
        </div>
      );
    }

    if (field.type === 'select') {
      return (
        <div key={field.id}>
          {field.label && <label style={labelStyle}>{field.label}{field.required && <span style={{ color: accentColor }}> *</span>}</label>}
          <div style={{ position: 'relative' }}>
            <select
              style={{ ...inputStyle, appearance: 'none', paddingRight: 28, cursor: 'pointer' }}
            >
              <option value="" style={{ background: '#1f2937' }}>{field.placeholder || 'Select an option'}</option>
              {(field.options || []).map((o) => (
                <option key={o.value} value={o.value} style={{ background: '#1f2937' }}>{o.label}</option>
              ))}
            </select>
            <span style={{
              position: 'absolute', right: 9, top: '50%', transform: 'translateY(-50%)',
              pointerEvents: 'none', fontSize: 10, color: 'rgba(255,255,255,0.5)',
            }}>▼</span>
          </div>
        </div>
      );
    }

    if (field.type === 'checkbox') {
      return (
        <label key={field.id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <input type="checkbox" readOnly style={{ accentColor, width: 14, height: 14, flexShrink: 0 }} />
          <span style={{ fontSize: 12, color: textColor, lineHeight: 1.3 }}>{field.label || 'Checkbox'}</span>
        </label>
      );
    }

    if (field.type === 'radio') {
      const options = field.options?.length
        ? field.options
        : [{ label: 'Option 1', value: 'opt1' }, { label: 'Option 2', value: 'opt2' }];
      return (
        <div key={field.id}>
          {field.label && <label style={labelStyle}>{field.label}{field.required && <span style={{ color: accentColor }}> *</span>}</label>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {options.map((o) => (
              <label key={o.value} style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer' }}>
                <input type="radio" readOnly name={field.name} style={{ accentColor, width: 13, height: 13, flexShrink: 0 }} />
                <span style={{ fontSize: 12, color: textColor }}>{o.label}</span>
              </label>
            ))}
          </div>
        </div>
      );
    }

    // text | email | phone | number | date | url
    const htmlType = field.type === 'phone' ? 'tel' : field.type;
    return (
      <div key={field.id}>
        {field.label && <label style={labelStyle}>{field.label}{field.required && <span style={{ color: accentColor }}> *</span>}</label>}
        <input
          type={htmlType}
          readOnly
          placeholder={field.placeholder}
          style={inputStyle}
        />
      </div>
    );
  }

  return (
    <div style={{
      width: '100%', height: '100%',
      background: bg, borderRadius,
      padding: '14px 14px',
      display: 'flex', flexDirection: 'column', gap: 9,
      border: '1px solid rgba(255,255,255,0.08)',
      backdropFilter: 'blur(16px)',
      overflow: 'hidden',
      boxSizing: 'border-box',
    }}>
      {/* Header badge */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 0 }}>
        <span style={{
          background: accentColor, color: 'white',
          fontSize: 8, fontWeight: '700', padding: '2px 7px',
          borderRadius: 4, textTransform: 'uppercase', letterSpacing: 1.2,
        }}>
          FORM
        </span>
        {formTitle && (
          <span style={{ fontSize: 12, fontWeight: 700, color: textColor, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {formTitle}
          </span>
        )}
      </div>

      {/* Fields */}
      {fields.filter(f => f.type !== 'hidden').map(renderField)}

      {!fields.length && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
          <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', textAlign: 'center' }}>
            Add form fields in the<br />Properties panel →
          </p>
        </div>
      )}
    </div>
  );
}

function hexToRgba(hex, opacity = 1) {
  if (!hex?.startsWith('#')) return hex || 'rgba(15,15,26,0.9)';
  const h = hex.replace('#', '');
  const r = Number.parseInt(h.slice(0, 2), 16);
  const g = Number.parseInt(h.slice(2, 4), 16);
  const b = Number.parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${opacity})`;
}
